import { Injectable } from '@angular/core';
import { environment } from 'src/environments/environment';
import { NavigationEnd, Router } from '@angular/router';
import { HelpersService } from '../helpers/helpers.service';
import { applyCspNonce } from '../../utils/csp-nonce.util';
import * as _ from 'lodash';
declare const gtag: Function;

// Service to handle Google Analytics integration
@Injectable({
  providedIn: 'root'
})
// GoogleAnalyticsService provides methods to initialize and track page views with Google Analytics
export class GoogleAnalyticsService {

  constructor(
    public router: Router,
    private helpersService: HelpersService
  ) {  }

  // Initialize Google Analytics tracking and listen to route changes
  initialize(){
    this.injectGAScript(environment.google_analytics_4.measurement_id);

    // Subscribe to router events to track page views
    this.router.events.subscribe((event) => {

      // Only act on NavigationEnd events (i.e., when navigation is complete)
      if (event instanceof NavigationEnd) {
        // Get custom parameters for Google Analytics
        const customParams = this.getCustomAnalyticsParams(event.urlAfterRedirects);
        
        // For now sending all page view to Google Analytics
        gtag('config', environment.google_analytics_4.measurement_id, { 
          'page_path': event.urlAfterRedirects,
        });
        
        gtag('event', 'page_view', {
          'page_location': document.location.origin + event.urlAfterRedirects,
          'page_title': document.title,
          'active_tab': customParams.tab_name
        });

        if (customParams.account) {
          gtag('event', 'account_view', {
            'account_name': customParams.account
          });          
        }

        if (customParams.project) {
          gtag('event', 'project_view', {
            'project_name': customParams.project
          });          
        }

        gtag('event', 'application_view', {
          'app_new': customParams.app_new,
          'version': customParams.version
        });

        gtag('event', 'user_view', {
          'user_id': customParams.user_id,
          'user_type': customParams.user_type,
          'user_domain': customParams.user_domain,
          'manager_type': customParams.manager_type
        });

      }
    })
  }

  // Get custom parameters for Google Analytics tracking
  private getCustomAnalyticsParams(url: string): any {
    const params: any = {};

    // app_new - 'Production' if env name is production
    if (environment.envName === 'prod_india') {
      params.app_new = 'Production';
    }

    // version - dashboard build version
    params.version = environment.version || '';

    // page_name - dashboard page name (extract from URL)
    params.page_name = this.getPageNameFromUrl(url);

    // account - selected account name
    const accountDetails = _.get(this.helpersService.helpers, 'accountDetails', {});
    params.account = accountDetails?.display_name || accountDetails?.name;

    // project - selected project name
    const brandDetails = _.get(this.helpersService.helpers, 'brandDetails', {});
    params.project = brandDetails?.display_name || brandDetails?.name;

    // user_type - internal/external based on email domain
    const managerDetails = _.get(this.helpersService.helpers, 'managerDetails.manager', {});
    const userEmail = managerDetails?.email_address;
    const userInfo = this.getUserType(userEmail);
    params.user_type = userInfo.user_type;
    params.user_domain = userInfo.user_domain;

    // manager_type - from manager details
    const loggedInManager = _.get(this.helpersService.helpers, 'managerDetails.loggedInManager', {});
    params.manager_type = loggedInManager?.manager_type || managerDetails?.manager_type;

    // user_id - ObjectId from logged in user details
    params.user_id = loggedInManager?._id || managerDetails?._id;

    // tab_name - active tab name from URL
    params.tab_name = this.getActiveTabFromUrl(url);

    return params;
  }

  // Extract page name from URL
  private getPageNameFromUrl(url: string): string {
    const urlParts = url.split('/').filter(part => part);
    
    if (urlParts.length === 0) return 'home';
    
    // Handle different URL patterns
    if (urlParts[0] === 'accounts') {
      if (urlParts.length === 1) return 'accounts-list';
      if (urlParts[1] === 'home') return 'account-home';
      if (urlParts[1] === 'create') return 'account-create';
      return urlParts[1] || 'accounts';
    }
    
    if (urlParts[0] === 'account' && urlParts[1] === 'project') {
      return urlParts[2] || 'project';
    }
    
    if (urlParts[0] === 'login') return 'login';
    if (urlParts[0] === 'dashboard') return 'dashboard';
    
    return urlParts[0] || 'home';
  }

  // Determine user type based on email domain
  private getUserType(email: string) {
    let userDetails = {
      user_type: '',
      user_domain: ''
    };
    if (!email) {
      userDetails.user_type = 'external';
      userDetails.user_domain = '';
      return userDetails;
    }
    const domain = email.split('@')[1];
    userDetails['user_domain'] = domain;
    if (domain === 'litmusworld.com') {
      userDetails['user_type'] = 'internal';
    } else {
      userDetails['user_type'] = 'external';
    }
    return userDetails;
  }

  // Extract active tab from URL
  private getActiveTabFromUrl(url: string): string {
    try {
      const urlObj = new URL(url, window.location.origin);
      return urlObj.searchParams.get('active_tab') || '';
    } catch (error) {
      return '';
    }
  }

  // Injects the Google Analytics script into the document head
  injectGAScript(gaId: string): void {
    if (!gaId) return;

    const script = document.createElement('script');
    applyCspNonce(script);
    script.src = `https://www.googletagmanager.com/gtag/js?id=${gaId}`;
    script.async = true;
    document.head.appendChild(script);

    const analyticsWindow = window as any;
    analyticsWindow.dataLayer = analyticsWindow.dataLayer || [];
    analyticsWindow.gtag = analyticsWindow.gtag || function() {
      analyticsWindow.dataLayer.push(arguments);
    };
    analyticsWindow.gtag('js', new Date());
    analyticsWindow.gtag('config', gaId);
  }

}
